import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router";
import { Outlet } from "react-router-dom";
// Homepage version switch (non-destructive): both versions are aliased to
// `Home`, so the `/` route element below stays the same — just swap which
// import line is active to switch versions.
//   - New homepage:     src/pages/dashboard/home-v2.tsx   (active)
//   - Current homepage: src/pages/dashboard/index.tsx
import { HomeV2 as Home } from "src/pages/dashboard/home-v2";
// import { Home } from "src/pages/dashboard/index";
import { Layout as DashboardLayout } from "src/layouts/dashboard";
import Onboarding from "src/pages/auth/jwt/onboarding";
import { ExpressLayout } from "src/layouts/dashboard/express-layout";
import { AccessLayout } from "src/layouts/dashboard/access-layout";
import { UserDataContexType } from "src/contexts/userData-context";
import { UserDataContext } from "src/contexts/userData-context";
import { UserDataProvider } from "src/contexts/userData-context";
import { HouseholdProvider } from "src/contexts/household-context";

const LegalPage = lazy(() => import("src/pages/dashboard/legal"));
const FinancialPage = lazy(() => import("src/pages/dashboard/financial"));
const PersonalPage = lazy(() => import("src/pages/dashboard/personal"));
const DigitalPage = lazy(() => import("src/pages/dashboard/digital"));
const PropertyPage = lazy(() => import("src/pages/dashboard/property"));
const LegacyPage = lazy(() => import("src/pages/dashboard/legacy"));
const MedicalPage = lazy(() => import("src/pages/dashboard/medical"));
const ExpressPage = lazy(() => import("src/pages/dashboard/express"));
const AccessPage = lazy(() => import("src/pages/dashboard/access"));

const WillsAndTrustsHome = lazy(
  () => import("src/pages/dashboard/documents/wills-and-trusts/index")
);
const WillsAndTrustsCreate = lazy(
  () => import("src/pages/dashboard/documents/wills-and-trusts/create")
);
const WillsAndTrustsExpress = lazy(
  () => import("src/pages/dashboard/documents/wills-and-trusts/express")
);
const PowerOfAttorneyHome = lazy(
  () => import("src/pages/dashboard/documents/power-of-attorney/index")
);
const PowerOfAttorneyCreate = lazy(
  () => import("src/pages/dashboard/documents/power-of-attorney/create")
);
const PowerOfAttorneyExpress = lazy(
  () => import("src/pages/dashboard/documents/power-of-attorney/express")
);
const HealthCareDirectiveHome = lazy(
  () => import("src/pages/dashboard/documents/health-care-directive/index")
);
const HealthCareDirectiveCreate = lazy(
  () => import("src/pages/dashboard/documents/health-care-directive/create")
);
const HealthCareDirectiveExpress = lazy(
  () => import("src/pages/dashboard/documents/health-care-directive/express")
);
const AssetsHome = lazy(
  () => import("src/pages/dashboard/documents/assets/index")
);
const AssetsExpress = lazy(
  () => import("src/pages/dashboard/documents/assets/express")
);

const PlanningDocumentsPage = lazy(
  () => import("src/pages/dashboard/planning/documents")
);
const PlanningAssetsPage = lazy(
  () => import("src/pages/dashboard/planning/assets")
);
const PlanningLifeDetailsPage = lazy(
  () => import("src/pages/dashboard/planning/life-details")
);
const PlanningLetterToFamilyPage = lazy(
  () => import("src/pages/dashboard/planning/letter-to-family")
);

// Other
const AccountPage = lazy(() => import("src/pages/dashboard/account"));

export const dashboardRoutes: RouteObject[] = [
  {
    path: "",
    element: (
      <DashboardLayout>
        <Suspense>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    ),
    children: [
      {
        index: true,
        path: "/",
        element: <Home />,
      },
      {
        path: "planning/documents",
        element: <PlanningDocumentsPage />,
      },
      {
        path: "planning/assets",
        element: <PlanningAssetsPage />,
      },
      {
        path: "planning/life-details",
        element: <PlanningLifeDetailsPage />,
      },
      {
        path: "planning/letter-to-family",
        element: <PlanningLetterToFamilyPage />,
      },
      {
        path: "account/:tab?",
        element: <AccountPage />,
      },
      {
        path: ":ud_id/legal",
        element: <LegalPage />,
      },
      {
        path: ":ud_id/medical",
        element: <MedicalPage />,
      },
      {
        path: ":ud_id/legacy",
        element: <LegacyPage />,
      },
      {
        path: ":ud_id/financial",
        element: <FinancialPage />,
      },
      {
        path: ":ud_id/property",
        element: <PropertyPage />,
      },
      {
        path: ":ud_id/financial",
        element: <FinancialPage />,
      },
      {
        path: ":ud_id/personal",
        element: <PersonalPage />,
      },
      {
        path: ":ud_id/digital",
        element: <DigitalPage />,
      },
      {
        path: ":ud_id/wills-and-trusts",
        element: <WillsAndTrustsHome />,
      },
      {
        path: ":ud_id/financial-power-of-attorney",
        element: <PowerOfAttorneyHome />,
      },
      {
        path: ":ud_id/health-care-directive",
        element: <HealthCareDirectiveHome />,
      },
      {
        path: ":ud_id/assets",
        element: <AssetsHome />,
      },
      {
        path: ":ud_id/wills-and-trusts/create/:estateDocId",
        element: <WillsAndTrustsCreate />,
      },
      {
        path: ":ud_id/financial-power-of-attorney/create/:estateDocId",
        element: <PowerOfAttorneyCreate />,
      },
      {
        path: ":ud_id/health-care-directive/create/:estateDocId",
        element: <HealthCareDirectiveCreate />,
      },

    ],

  }, {
    path: "onboarding",
    element: <UserDataProvider>
      <UserDataContext.Consumer>
        {(userDataContext: UserDataContexType) => {
          return (
            <HouseholdProvider userData={userDataContext.userData || null} ownerUserData={userDataContext.ownerUserData || null} updateProHouseholdContext={userDataContext.updateProHouseholdContext || null}>
              <Suspense>
                <Onboarding />
              </Suspense>
            </HouseholdProvider>
          );
        }}
      </UserDataContext.Consumer>
    </UserDataProvider>,
  }, {
    path: "express",
    element: (
      <UserDataProvider>
      <UserDataContext.Consumer>
        {(userDataContext: UserDataContexType) => {
          return (
            <HouseholdProvider userData={userDataContext.userData || null} ownerUserData={userDataContext.ownerUserData || null} updateProHouseholdContext={userDataContext.updateProHouseholdContext || null}>
              <ExpressLayout>
                <Suspense>
                  <Outlet />
                </Suspense>
              </ExpressLayout>
            </HouseholdProvider>
          );
        }}
      </UserDataContext.Consumer>
    </UserDataProvider>
    ),
    children: [
      {
        index: true,
        path: "",
        element: <ExpressPage />,
      },
      {
        path: "account",
        element: <AccountPage />,
      },
      {
        path: ":ud_id/wills-and-trusts/create/:estateDocId",
        element: <WillsAndTrustsExpress />,
      },
      {
        path: ":ud_id/financial-power-of-attorney/create/:estateDocId",
        element: <PowerOfAttorneyExpress />,
      },
      {
        path: ":ud_id/health-care-directive/create/:estateDocId",
        element: <HealthCareDirectiveExpress />,
      },
      {
        path: ":ud_id/assets",
        element: <AssetsExpress />,
      },
    ],
  },
  {
    path: ":ud_id/access",
    element: (
      <AccessLayout>
        <AccessPage />
      </AccessLayout>
    ),
  },

];

